import React, { useRef, useEffect, useState } from 'react';
import { snapshot_ChartHeaderButtons as ChartHeaderButtons } from './snapshot_ChartHeaderButtons';
import useResizeObserver from '@/hooks/useResizeObserver';
import { useSnapshotRow } from '../contexts/SnapshotRowContext';
import { useShareUrl } from '@/hooks/useShareUrl';
import { useDownloadWithAuth } from '@/hooks/useDownloadWithAuth';
import { downloadChartSection, downloadAllChartsWithWatermark, downloadChartWithCanvasWatermark } from '@/utils/chartDownload';
import { createChartId } from '@/utils/shareUtils';
import { snapshotMetricDefs } from '../data/SnapshotAboutTheDataDefs';

interface ChartCardProps {
  title: string;
  description: string;
  children: React.ReactNode;
  className?: string;
  rowIndex: number;
  cardIndex: number;
  chartName?: string;
  shareLink?: string;
}

export const snapshot_ChartCard: React.FC<ChartCardProps> = ({
  title,
  description,
  children,
  className = "",
  rowIndex,
  cardIndex,
  chartName,
  shareLink
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadPopupOpen, setIsDownloadPopupOpen] = useState(false);
  
  const headerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HTMLDivElement>(null);
  
  const { rowHeights, headerHeights, updateHeight, updateHeaderHeight, removeCard } = useSnapshotRow();
  const { height: headerHeight } = useResizeObserver(headerRef);
  const { height: contentHeight } = useResizeObserver(contentRef);
  
  // Use provided shareLink or fall back to current URL
  const currentShareLink = useShareUrl();
  const { executeWithAuth } = useDownloadWithAuth();
  const finalShareLink = shareLink || currentShareLink;
  const finalChartName = chartName || title;
  const metricDef = snapshotMetricDefs[finalChartName as keyof typeof snapshotMetricDefs];
  
  // Download functionality - using canvas watermark to avoid DOM layout interference
  const downloadSingleChart = async () => {
    if (!chartRef.current) {
      console.log('No chart ref found');
      return;
    }
    
    console.log('Starting download for snapshot chart:', title);
    setIsDownloading(true);
    
    try {
      // Use canvas-based watermark to avoid DOM layout interference with our positioning fixes
      await downloadChartWithCanvasWatermark(chartRef.current, `${finalChartName.toLowerCase().replace(/\s+/g, '-')}`, {
        watermarkOptions: {
          opacity: 1.0, // Testing: 100% opacity to debug visibility
          size: { width: 200, height: 60 }
        },
        includeTimestamp: false
      });
    } catch (error) {
      console.error('Error in snapshot chart download:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadAllCharts = async () => {
    console.log('[downloadAllCharts] Starting individual chart downloads with watermarks');
    setIsDownloading(true);
    try {
      await downloadAllChartsWithWatermark(
        'snapshot-charts-section',
        'snapshot-charts',
        {
          watermarkOptions: {
            opacity: 0.5,
            size: { width: 300, height: 90 }
          },
          useCanvasWatermark: true, // Use canvas watermarks for consistency with individual downloads
          includeTimestamp: true
        }
      );
    } catch (error) {
      console.error('[downloadAllCharts] Error:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  // Download entire section as single image
  const downloadSectionAsImage = async () => {
    console.log('[downloadSectionAsImage] Capturing full snapshot chart section');
    setIsDownloading(true);
    try {
      const section = document.getElementById('snapshot-charts-section');
      if (!section) {
        console.warn('Snapshot chart section not found');
        return;
      }
      await downloadChartSection(section, 'snapshot-charts');
    } catch (error) {
      console.error('[downloadSectionAsImage] Error:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadOption = async (option: 'single' | 'all') => {
    setIsDownloadPopupOpen(false);
    if (option === 'single') {
      await executeWithAuth(downloadSingleChart, 'single');
    } else {
      await executeWithAuth(downloadSectionAsImage, 'all'); // Back to section-as-one-canvas
    }
  };

  const handleDownloadButtonClick = () => {
    if (isDownloading) return; // guard against concurrent downloads
    setIsDownloadPopupOpen(true);
  };

  useEffect(() => {
    updateHeight(rowIndex, cardIndex, headerHeight);
  }, [updateHeight, rowIndex, cardIndex, headerHeight]);

  useEffect(() => {
    updateHeaderHeight(rowIndex, cardIndex, contentHeight);
  }, [updateHeaderHeight, rowIndex, cardIndex, contentHeight]);

  useEffect(() => {
    // Cleanup function
    return () => {
      removeCard(rowIndex, cardIndex);
    };
  }, [removeCard, rowIndex, cardIndex]);

  // Get the maximum heights for this row
  const rowMaxHeight = rowHeights.get(rowIndex) || 0;
  const headerMaxHeight = headerHeights.get(rowIndex) || 0;

  return (
    <article 
      ref={chartRef}
      id={createChartId(finalChartName, 'sn')}
      className={`bg-white w-full min-w-[280px] rounded-[20px] relative flex flex-col h-full ${className}`}
      data-chart-title={finalChartName}
    >
      <header 
        ref={headerRef} 
        className="w-full px-6 relative max-md:max-w-full max-md:px-5 flex-shrink-0 flex flex-col after:content-[''] after:absolute after:bottom-0 after:inset-x-6 after:max-md:inset-x-5 after:h-[4px] after:bg-gradient-to-r after:from-[#CA0079] after:to-[#F1AB11] after:rounded-full"
        style={{ 
          minHeight: `${rowMaxHeight}px`,
          transition: 'min-height 0.2s ease-in-out',
          paddingTop: '24px',
          paddingBottom: '24px'
        }}
      >
        <div 
          ref={contentRef}
          className="w-full flex flex-col"
          style={{
            minHeight: `${headerMaxHeight}px`,
            transition: 'min-height 0.2s ease-in-out'
          }}
        >
          <div className="w-full max-md:max-w-full">
            <div className="flex flex-row-reverse flex-wrap items-start justify-between gap-x-6 gap-y-4 mb-3">
              <ChartHeaderButtons 
                chartName={finalChartName} 
                shareLink={finalShareLink}
                isDownloading={isDownloading}
                isDownloadPopupOpen={isDownloadPopupOpen}
                onDownloadClick={handleDownloadButtonClick}
                onDownloadOption={handleDownloadOption}
                onDownloadPopupClose={() => setIsDownloadPopupOpen(false)}
                metricDefinition={metricDef}
                aboutDescription={description}
              />
              <h2 className="text-[#212121] text-[28px] font-semibold leading-tight align-middle chart-text-shift flex-1 min-w-[200px]" style={{lineHeight: '1.2', verticalAlign: 'middle'}}>
                {title}
              </h2>
            </div>

            <p className="text-[#212121] text-xl font-normal leading-tight align-middle mt-3 max-md:max-w-full chart-text-shift" style={{lineHeight: '1.2', verticalAlign: 'middle'}}>
              {description}. Brought to you by Govstream.ai.
            </p>
          </div>
        </div>
      </header>
      <div className="flex-1 px-6 flex flex-col justify-end max-md:px-5 max-donut:px-4">
        {children}
      </div>
    </article>
  );
};